import i18n from 'i18next';
import { Sender } from 'xstate';
import { sessionId } from '../../apollo';
import { AppChannel } from '../../constants';
import { CustomerFlowType } from '../../constants/customer-flow-type';
import { Application, ChannelTypes, Dealer } from '../../gql/graphql';
import { Role } from '../../types/role';
import { CustomerFlowTypeMachineContext } from '../types/shared/context/customerFlowTypeMachineContext';
import { OrderIdMachineContext } from '../types/shared/context/orderIdMachineContext';
import { OrderMachineContext } from '../types/shared/context/orderMachineContext';
import { RoleMachineContext } from '../types/shared/context/roleMachineContext';

type SegmentContext = OrderMachineContext &
  OrderIdMachineContext &
  RoleMachineContext &
  CustomerFlowTypeMachineContext & {
    entrypointParam?: string;
    entrypointSessionId?: string;
  };

// Use these types when we fix state machine typing
export type trackEventPayload = { data: { eventName: string; payload?: object } };
export type pageEventPayload = { data: { pageName: string; payload?: object } };
export type identifyEventPayload = { data: { customerSorId: number; payload?: object } };

export type SegmentProps = {
  track: (eventName: string, payload?: object) => void;
  page: (pageName: string, payload?: object) => void;
  identify: (customerSorId: number, payload: object) => void;
};

export const getSegmentEventEmitters = (sendFn: Sender<any>) => ({
  track: (eventName: string, payload?: object) => sendFn({ type: 'TRACK', data: { eventName, payload } }),
  page: (pageName: string, payload?: object) => sendFn({ type: 'PAGE', data: { pageName, payload } }),
  identify: (customerSorId: number, payload?: object) => sendFn({ type: 'IDENTIFY', data: { customerSorId, payload } }),
});

export const track = (eventName: string, payload?: object) => {
  return (context: SegmentContext, event: any) => {
    window.analytics.track(eventName, {
      ...getDefaultProperties(
        context.orderId,
        context.application,
        context.dealer,
        context.role,
        context.customerFlowType,
        context.entrypointParam,
        context.entrypointSessionId,
      ),
      ...payload,
    });
  };
};

// Bandaid for when tracking through the machine causes an undesired rerender. Prefer the other method.
// Callers outside the machine must pass entrypointParam/entrypointSessionId so these props are not
// dropped from events — that is the path that caused the original tracking bug.
export const tempTrack = (
  orderId: string,
  application: Application | undefined,
  dealer: Dealer,
  role: Role,
  customerFlowType?: CustomerFlowType,
  entrypointParam?: string,
  entrypointSessionId?: string,
) => {
  return (eventName: string, payload?: object) => {
    window.analytics.track(eventName, {
      ...getDefaultProperties(orderId, application, dealer, role, customerFlowType, entrypointParam, entrypointSessionId),
      ...payload,
    });
  };
};

var previousPage = 'none';

// `previousPage` holds the name of the last issued `page()` event, which until the next one fires - is also the current page.
export const getCurrentPageName = () => previousPage;

export const page = (pageName: string, payload?: object) => (context: SegmentContext, event: any) => {
  window.analytics.page(pageName, {
    ...getDefaultProperties(
      context.orderId,
      context.application,
      context.dealer,
      context.role,
      context.customerFlowType,
      context.entrypointParam,
      context.entrypointSessionId,
    ),
    referrer: document.referrer,
    previousPage,
    ...payload,
  });
  previousPage = pageName;
};

export const identify = (customerSorId?: number, payload?: object) => {
  window.analytics.identify(customerSorId ? `customer-${customerSorId}` : undefined, payload);
};

const getDefaultProperties = (
  orderId: string,
  application: Application | undefined,
  dealer: Dealer,
  role: Role,
  customerFlowType: CustomerFlowType | undefined,
  entrypointParam?: string,
  entrypointSessionId?: string,
) => {
  const appChannel = dealer?.isVirtualCard
    ? AppChannel.virtualCard
    : dealer.channelType === ChannelTypes.BrickAndMortar
    ? AppChannel.brickAndMortar
    : AppChannel.ecomm;

  return {
    appChannel,
    applicationId: application?.lease?.id || application?.loan?.id,
    applicationNumber: application?.lease?.displayId,
    appLanguage: i18n.resolvedLanguage ?? 'en',
    browserLanguage: navigator.language || navigator.languages?.[0] || null,
    campaignMedium: new URLSearchParams(document.location.search.substring(1)).get('cm') || undefined,
    companyDealerId: dealer?.companyDealerId,
    dealerId: dealer.id,
    formType: dealer?.formType,
    initiatedBy: `${role}`,
    orderFinancingType: dealer.financingType,
    orderId,
    sessionId: sessionId,
    entrypointSessionId: entrypointSessionId,
    entrypoint: entrypointParam,
    flowType: getFlowType(application, customerFlowType),
  };
};

const getFlowType = (application: Application | undefined, customerFlowType: CustomerFlowType | undefined): string | undefined => {
  const isOtb = !!application?.lease?.isOtb || !!application?.loan?.isOtb; // otb flows immediately create an app as soon as it knows it's otb
  if (isOtb) return 'availableApproval';

  const isSubsequentApp = parseInt(application?.lease?.displayId?.split('-')?.[1]!) > 1; // returns false when there's no app
  if (isSubsequentApp) return 'prefilled';
  const isFirstApp = parseInt(application?.lease?.displayId?.split('-')?.[1]!) === 1; // returns false when there's no app
  if (isFirstApp) return 'new';

  const isVerifiedCustomer = customerFlowType === CustomerFlowType.VerifiedCustomer; // represents a fully recognized (phone + last4) customer
  if (isVerifiedCustomer) return 'prefilled';
  const isNewCustomer = customerFlowType === CustomerFlowType.NewCustomer; // represents an unrecognized customer
  if (isNewCustomer) return 'new';

  return undefined;
};
